import React, { Component, useRef } from "react";
import {
  Fab,
  IconButton,
  Typography,
  Paper,
  Menu,
  Button,
  MenuItem,
  ListItemIcon,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import {
  ChevronRight as ChevronRightIcon,
  Menu as MenuIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  UploadFile,
  DriveFolderUpload,
} from "@mui/icons-material";
import DetailSidebarContent from "./DetailSidebarContent";
import MainSidebarHeader from "./MainSidebarHeader";
import MainSidebarContent from "./MainSidebarContent";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import Sidebar from "./Sidebar";
import DetailSidebarHeader from "./DetailSidebarHeader";
import RecycleBinView from "./RecycleBinView";
import RecycleBinHeader from "./RecycleBinHeader";
import FileExplorerHeader from "./FileExplorerHeader";
import { api } from "../../../../../Assets/api/api";
import { toast } from "react-toastify";
import { AlertDialog } from "../../../../../components/Dialog/AlertDialog";
import { RenameDialog } from "../../../../../components/Dialog/RenameDialog";
import {
  SearchSortProvider,
  useSearchSort,
} from "../context/SearchSortContext";
import FileExplorerTable from "./FileExplorerTable";
import { deductStorageForFile } from "../../../../../utils/storageUsage";

// Inner component that uses the context
const FileManagerAppInner = ({ user, ...props }) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const { t } = useTranslation();
  const translation = t("fileExplorer");
  const [leftSidebarOpen, setLeftSidebarOpen] = React.useState(false);
  const [rightSidebarOpen, setRightSidebarOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [files, setFiles] = React.useState([]);
  const [folders, setFolders] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [viewType, setViewType] = React.useState("list");
  const [currentFolder, setCurrentFolder] = React.useState(null);
  const [breadcrumbPath, setBreadcrumbPath] = React.useState([]);
  const [folderDeleteState, setFolderDeleteState] = React.useState({
    open: false,
    loading: false,
    folder: null,
  });
  const [renameDialogState, setRenameDialogState] = React.useState({
    open: false,
    loading: false,
    item: null,
    isFolder: false,
  });
  const [isRecycleBinView, setIsRecycleBinView] = React.useState(false);

  // Get search and sort state from context
  const {
    searchText,
    setSearchText,
    debouncedSearchText,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    page,
    setPage,
    limit,
    setLimit,
    currentView,
    switchView,
  } = useSearchSort();

  const [explorerItems, setExplorerItems] = React.useState([]);
  const [explorerTotal, setExplorerTotal] = React.useState(0);
  const requestIdRef = React.useRef(0);

  const slug = user?.slug;
  const isValidSlug = slug && typeof slug === "string" && slug.length > 0;

  if (!user || !isValidSlug) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <Typography>Loading user information...</Typography>
      </Box>
    );
  }

  const getFilesForTable = React.useCallback(
    async ({ page, limit, sortBy, sortOrder }) => {
      if (!user || !isValidSlug) {
        return { data: { data: { folders: [], files: [], total: 0 } } };
      }
      const queryParams = new URLSearchParams();
      if (currentFolder)
        queryParams.append("parentFolder", String(currentFolder));
      if (debouncedSearchText)
        queryParams.append("search", debouncedSearchText);
      queryParams.append("sortBy", sortBy);
      queryParams.append("sortOrder", sortOrder);
      queryParams.append("page", page);
      queryParams.append("limit", limit);
      if (slug) {
        const slugValue = slug.replace("?slug=", "");
        queryParams.append("slug", slugValue);
      }
      return await api.get(`/api/account-storage?${queryParams.toString()}`);
    },
    [user, isValidSlug, currentFolder, debouncedSearchText, slug]
  );

  const fetchExplorer = React.useCallback(async () => {
    if (!user || !isValidSlug || currentView !== "fileExplorer") return;
    const currentId = ++requestIdRef.current;
    try {
      setLoading(true);
      const res = await getFilesForTable({ page, limit, sortBy, sortOrder });
      if (requestIdRef.current !== currentId) return;
      const {
        folders = [],
        files = [],
        total = folders.length + files.length,
      } = res?.data?.data || {};
      const mappedFolders = folders.map((f) => ({
        ...f,
        type: "folder",
        isFolder: true,
      }));
      const mappedFiles = files.map((f) => ({ ...f, isFolder: false }));
      const combined = [...mappedFolders, ...mappedFiles];
      setExplorerItems(combined);
      setExplorerTotal(typeof total === "number" ? total : combined.length);
    } catch (e) {
      if (requestIdRef.current !== currentId) return;
      console.error(e);
    } finally {
      if (requestIdRef.current === currentId) setLoading(false);
    }
  }, [
    user,
    isValidSlug,
    currentView,
    page,
    limit,
    sortBy,
    sortOrder,
    getFilesForTable,
  ]);

  // Single effect to fetch explorer items; dependencies are stable through useCallback
  React.useEffect(() => {
    fetchExplorer();
  }, [fetchExplorer]);

  React.useEffect(() => {
    // Auto-open right sidebar when files are selected
    if (selectedFiles.length > 0) {
      setRightSidebarOpen(true);
    }
  }, [selectedFiles]);

  // Close sidebars when screen size changes to small
  React.useEffect(() => {
    if (!isLargeScreen) {
      setLeftSidebarOpen(false);
      setRightSidebarOpen(false);
    }
  }, [isLargeScreen]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if clicking on the main content area, not on sidebars
      const clickedInsideSidebar =
        event.target.closest("[data-sidebar]") !== null;
      const clickedSidebarButton =
        event.target.closest('button[aria-label*="sidebar"]') !== null;
      const clickedInDialog =
        event.target.closest('[role="dialog"]') !== null ||
        event.target.closest(".MuiModal-root") !== null ||
        event.target.closest(".MuiDialog-root") !== null;

      if (!clickedInsideSidebar && !clickedSidebarButton && !clickedInDialog) {
        if (leftSidebarOpen) setLeftSidebarOpen(false);
        if (rightSidebarOpen) setRightSidebarOpen(false);
      }
    };

    if (leftSidebarOpen || rightSidebarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [leftSidebarOpen, rightSidebarOpen]);

  const toggleLeftSidebar = () => {
    setLeftSidebarOpen((prev) => !prev);
  };

  const toggleRightSidebar = () => {
    setRightSidebarOpen((prev) => !prev);
  };

  const handleRecycleBinClick = () => {
    setIsRecycleBinView(true);
    setSelectedFiles([]);
    setRightSidebarOpen(false);
    switchView("recycleBin");
  };

  const handleBackToFiles = () => {
    setIsRecycleBinView(false);
    setSelectedFiles([]);
    switchView("fileExplorer");
    setPage(0);
    // Centralized effect will fetch based on currentFolder/page
  };

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const handleAddClick = (event) => {
    setAnchorEl(event.currentTarget); // Open menu
  };

  const handleClose = () => {
    setAnchorEl(null); // Close menu
  };

  const handleFileClick = () => {
    handleClose();
    fileInputRef.current?.click();
  };

  const handleFolderClick = () => {
    handleClose();
    folderInputRef.current?.click();
  };

  const openRenameFolderDialog = (item) => {
    setRenameDialogState({
      open: true,
      loading: false,
      item,
      isFolder: item.type === "folder", // or however you identify folders
    });
  };
  const openDeleteFolderDialog = (folder) => {
    setFolderDeleteState({
      open: true,
      loading: false,
      folder,
    });
  };

  const deleteCurrentFolder = async (specificFolder = null) => {
    // Don't proceed if user or slug is not available
    if (!user || !isValidSlug) {
      toast.error("User information not available");
      return;
    }

    try {
      setFolderDeleteState((s) => ({ ...s, loading: true }));

      let folderPath = "";
      let folderName = "";

      if (specificFolder) {
        if (specificFolder.path) {
          folderPath = specificFolder.path;
        } else {
          // Fallback: manually construct path using breadcrumb
          const segments = breadcrumbPath.map((f) => f.name);
          folderName = specificFolder.name;
          folderPath = segments.length
            ? `${segments.join("/")}/${folderName}`
            : folderName;
        }
      } else {
        // Deleting current folder
        const segments = breadcrumbPath.map((f) => f.name);
        folderPath = segments.join("/");
      }

      const encodedPath = encodeURIComponent(folderPath);
      const response = await api.delete(
        `/api/account-storage/${encodedPath}${slug}`
      );

      const success =
        response?.status === 200 ||
        response?.status === 204 ||
        response?.data?.success === true ||
        response?.data?.sucess === "true";

      if (success) {
        toast.success(response?.data?.message || "Folder moved to recycle bin");

        if (specificFolder) {
          await fetchExplorer(page, limit, sortBy, sortOrder);
        } else {
          const parentPath = breadcrumbPath.slice(0, -1);
          const parentId = parentPath.length
            ? parentPath[parentPath.length - 1].id
            : null;
          setSelectedFiles([]);
          setRightSidebarOpen(false);
          await fetchExplorer(page, limit, sortBy, sortOrder);
        }
      } else {
        toast.error(
          response?.data?.message || "Failed to move folder to recycle bin"
        );
      }
    } catch (err) {
      console.error("Delete folder error:", err);
      toast.error(
        err?.response?.data?.message || "Error moving folder to recycle bin"
      );
    } finally {
      setFolderDeleteState({ open: false, loading: false, folder: null });
    }
  };

  const handleFolderNavigate = (folder) => {
    const updatedPath = [
      ...breadcrumbPath,
      { id: String(folder.id), name: folder.name },
    ];
    setPage(0);
    setCurrentFolder(String(folder.id));
    setBreadcrumbPath(updatedPath);
  };

  const handleRenameFolder = async (folder, newName) => {
    // Don't proceed if user or slug is not available
    if (!user || !isValidSlug) {
      toast.error("User information not available");
      return;
    }

    try {
      // Set loading true
      setRenameDialogState((s) => ({ ...s, loading: true }));

      const segments = breadcrumbPath.map((f) => encodeURIComponent(f.name));
      const folderPath = segments.join("/");

      const endpoint = `/api/account-storage/rename${slug}`;

      const response = await api.post(endpoint, {
        containerName: slug.replace("?slug=", ""),
        oldBlobName: folderPath ? `${folderPath}/${folder.name}` : folder.name,
        newBlobName: folderPath ? `${folderPath}/${newName}` : newName,
        slug: slug.replace("?slug=", ""),
      });

      const success =
        response?.status === 200 || response?.data?.success === true;

      if (success) {
        toast.success(response?.data?.message || "Folder renamed successfully");
        await fetchExplorer(page, limit, sortBy, sortOrder);

        // Close dialog and reset loading
        setRenameDialogState({
          open: false,
          loading: false,
          item: null,
          isFolder: false,
        });
      } else {
        toast.error(response?.data?.message || "Failed to rename folder");
        setRenameDialogState((s) => ({ ...s, loading: false }));
      }
    } catch (err) {
      console.error("Rename folder error:", err);
      toast.error(err?.response?.data?.message || "Error renaming folder");
      setRenameDialogState((s) => ({ ...s, loading: false }));
    }
  };

  const handleUpload = async (event) => {
    // Don't proceed if user or slug is not available
    if (!user || !isValidSlug) {
      toast.error("User information not available");
      return;
    }

    const input = event.target;
    const files = input.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    const currentPathParts = [...breadcrumbPath.map((f) => f.name)];
    const currentPath = currentPathParts.filter(Boolean).join("/");

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const relativePath =
        file.webkitRelativePath && file.webkitRelativePath.length > 0
          ? file.webkitRelativePath
          : file.name;

      const fullPath = currentPath
        ? `${currentPath}/${relativePath}`
        : relativePath;

      formData.append("files", file);
      formData.append("paths", fullPath);
    }

    try {
      setLoading(true);
      const response = await api.post(
        `/api/account-storage/upload${slug}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const success =
        response?.status === 200 ||
        response?.status === 201 ||
        response?.data?.success === true;
      if (success) {
        toast.success(response?.data?.message || "Upload completed");
        // Deduct storage per file
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          try {
            // Validate that user has required properties for storage deduction
            if (
              user &&
              (user.mail || user.username || user.userPrincipalName)
            ) {
              // Validate that file has size property
              if (file.size && !isNaN(file.size) && file.size > 0) {
                // Validate that slug is available
                if (slug && typeof slug === "string" && slug.length > 0) {
                  await deductStorageForFile({ file, user, slug });
                }
              }
            }
          } catch (error) {
            console.error(
              "Error deducting storage for file:",
              file.name,
              error
            );
            // Non-blocking - don't fail upload if storage deduction fails
          }
        }
      } else {
        toast.error(response?.data?.message || "Upload failed");
      }
      fetchExplorer(page, limit, sortBy, sortOrder);
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err?.response?.data?.message || "Upload error");
    } finally {
      setLoading(false);
      input.value = null;
    }
  };
  const handleSortChange = (column) => {
    const isAsc = sortBy === column && sortOrder === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setSortBy(column);
    setSortOrder(newOrder);
    setPage(0); // reset to first page
    fetchExplorer(page, limit, sortBy, sortOrder); // Call your fetch
  };
  function Breadcrumb({ className }) {
    return (
      <Box
        className={className}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: { xs: 14, sm: 16 },
            fontWeight: 500,
            color: "text.primary",
            whiteSpace: "nowrap",
            lineHeight: 1,
            mb: 0,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={() => {
            setPage(0);
            setCurrentFolder(null);
            setBreadcrumbPath([]);
          }} // Root level
        >
          {translation.myFiles}
        </Typography>
        {breadcrumbPath.map((folder, i) => (
          <React.Fragment key={folder.id}>
            <ChevronRightIcon sx={{ color: "text.secondary" }} />
            <Typography
              component="span"
              sx={{
                fontSize: { xs: 14, sm: 16 },
                fontWeight: 500,
                color:
                  i === breadcrumbPath.length - 1
                    ? "text.primary"
                    : "primary.main",
                whiteSpace: "nowrap",
                lineHeight: 1,
                mb: 0,
                cursor: i === breadcrumbPath.length - 1 ? "default" : "pointer",
                "&:hover": {
                  textDecoration:
                    i === breadcrumbPath.length - 1 ? "none" : "underline",
                },
              }}
              onClick={() => {
                if (i === breadcrumbPath.length - 1) return;
                const clickedFolder = breadcrumbPath[i];
                const nextPath = breadcrumbPath.slice(0, i + 1);
                setPage(0);
                setCurrentFolder(String(clickedFolder.id));
                setBreadcrumbPath(nextPath);
              }}
            >
              {folder.name}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
    );
  }

  // Render the default header content (always show this, sidebars will overlay)
  const renderHeaderContent = () => {
    if (isRecycleBinView) {
      return (
        <RecycleBinHeader
          onBackToFiles={() => setIsRecycleBinView(false)}
          slug={slug}
          onActionComplete={() => fetchExplorer(page, limit, sortBy, sortOrder)}
        />
      );
    }

    // Create the search and sort controls for the right side
    const searchAndSortControls = (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <TextField
          placeholder={translation.searchPlaceholder}
          size="small"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: 140, sm: 200 } }}
        />
      </Box>
    );

    // Create the add button and menu
    const addButtonAndMenu = (
      <>
        {/* Hidden folder input */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          multiple
          onChange={handleUpload}
        />
        <input
          type="file"
          ref={folderInputRef}
          style={{ display: "none" }}
          multiple
          webkitdirectory=""
          onChange={handleUpload}
        />

        {/* Main Add Button */}
        <Fab
          size="small"
          sx={{
            backgroundColor: "#6264a7",
            color: "white",
            "&:hover": { backgroundColor: "#4a4c85" },
          }}
          onClick={handleAddClick}
        >
          <AddIcon fontSize="small" />
        </Fab>

        {/* Menu with options */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={handleFileClick}>
            <ListItemIcon>
              <UploadFile fontSize="small" sx={{ mr: 1 }} />
            </ListItemIcon>
            {translation.uploadFile}
          </MenuItem>
          <MenuItem onClick={handleFolderClick}>
            <ListItemIcon>
              <DriveFolderUpload fontSize="small" sx={{ mr: 1 }} />
            </ListItemIcon>
            {translation.uploadFolder}
          </MenuItem>
        </Menu>
      </>
    );

    // Combine the right side controls
    const rightContent = (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          visibility: rightSidebarOpen ? "hidden" : "visible",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          justifyContent: { xs: "flex-end", sm: "flex-start" },
        }}
      >
        {searchAndSortControls}
        {addButtonAndMenu}
      </Box>
    );

    // Create the left side content with menu button and breadcrumb
    // const leftContent = (
    //   <Box
    //     sx={{
    //       display: "flex",
    //       flex: 1,
    //       pl: { xs: 1, sm: 3 },
    //       alignItems: "center",
    //       minWidth: 0,
    //     }}
    //   >
    //     <Box sx={{ display: { xs: "none", sm: "block" }, mr: 1 }}>
    //       <Breadcrumb />
    //     </Box>
    //     <Box sx={{ display: { xs: "block", sm: "none" } }}>
    //       <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
    //         {translation.myFiles}
    //       </Typography>
    //     </Box>
    //   </Box>
    // );

    return (
      <FileExplorerHeader
        title={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              gap: 1,
            }}
          >
            <IconButton
              onClick={toggleLeftSidebar}
              aria-label={translation.openLeftSidebar}
              edge="start"
              sx={{
                ml: 0,
                mr: 1,
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box
              sx={{
                overflow: "hidden",
                minWidth: 0,
              }}
            >
              <Breadcrumb />
            </Box>
          </Box>
        }
        rightContent={rightContent}
      />
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 0,
      }}
    >
      {/* Header - content changes based on sidebar state */}
      <Paper
        elevation={0}
        sx={{
          height: "73px",
          minHeight: "73px",
          maxHeight: "73px",
          p: { xs: 1, sm: 2 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          backgroundColor: "#fafafa",
          borderBottom: "1px solid #e0e0e0",
          flexShrink: 0,
          zIndex: 15, // Lower z-index so sidebar headers can overlay
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0,
            width: "100%",
            height: "100%",
          }}
        >
          {renderHeaderContent()}
        </Box>
      </Paper>

      {/* Main Content Area */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          position: "relative",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        {/* Left Sidebar - without header since it's now in main header */}
        <Sidebar
          open={leftSidebarOpen}
          onClose={toggleLeftSidebar}
          side="left"
          width={280}
          topOffset={-73} // Overlap main header
          header={<MainSidebarHeader />}
        >
          <MainSidebarContent onRecycleBinClick={handleRecycleBinClick} />
        </Sidebar>

        {/* Main Content */}
        <Box
          data-main-content
          sx={{
            flex: 1,
            overflow: "auto",
            minHeight: 0,
          }}
        >
          {isRecycleBinView ? (
            <RecycleBinView
              viewType={viewType}
              user={user}
              onBackToFiles={handleBackToFiles}
              onActionComplete={() =>
                fetchExplorer(page, limit, sortBy, sortOrder)
              }
            />
          ) : (
            <FileExplorerTable
              items={explorerItems}
              viewType={viewType}
              onSortChange={(column) => {
                if (sortBy === column)
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                else {
                  setSortBy(column);
                  setSortOrder("asc");
                }
              }}
              page={page}
              rowsPerPage={limit}
              onPageChange={(event, newPage) => {
                setPage(newPage);
              }}
              onRowsPerPageChange={(event) => {
                const newLimit = parseInt(event.target.value, 10);
                if (!isNaN(newLimit)) {
                  setLimit(newLimit);
                  setPage(0);
                }
              }}
              loading={loading}
              totalItems={explorerTotal}
              isSelected={(item) =>
                selectedFiles.some((file) => file.id === item.id)
              }
              onItemClick={(item) => {
                if (item.type === "folder") handleFolderNavigate(item);
                else setSelectedFiles([item]);
              }}
              viewMode="fileExplorer"
              onFolderNavigate={handleFolderNavigate}
              onRenameFolder={openRenameFolderDialog}
              onDeleteFolder={openDeleteFolderDialog}
              setCurrentFolder={setCurrentFolder}
              user={user}
              onActionComplete={() =>
                fetchExplorer(page, limit, sortBy, sortOrder)
              }
            />
          )}
        </Box>

        {/* Right Sidebar - without header since it's now in main header */}
        <Sidebar
          open={rightSidebarOpen}
          onClose={toggleRightSidebar}
          side="right"
          width={320}
          topOffset={-73} // Overlap main header
          header={
            <DetailSidebarHeader
              file={selectedFiles[0]}
              slug={slug}
              refreshFiles={() => fetchExplorer(page, limit, sortBy, sortOrder)}
              onDeleted={() => {
                setSelectedFiles([]);
                setRightSidebarOpen(false);
              }}
            />
          }
        >
          <DetailSidebarContent file={selectedFiles[0]} />
        </Sidebar>
      </Box>

      <RenameDialog
        open={renameDialogState.open}
        loading={renameDialogState.loading}
        handleClose={() => setRenameDialogState((s) => ({ ...s, open: false }))}
        handleRename={(newName) => {
          if (!renameDialogState.loading) {
            handleRenameFolder(renameDialogState.item, newName);
          }
        }}
        initialName={renameDialogState.item?.name || ""}
        isFolder={renameDialogState.isFolder}
      />

      {/* Delete Folder Dialog */}
      <AlertDialog
        open={folderDeleteState.open}
        loading={folderDeleteState.loading}
        handleClose={() =>
          setFolderDeleteState({ ...folderDeleteState, open: false })
        }
        handleOk={async () => {
          setFolderDeleteState((prev) => ({ ...prev, loading: true }));
          try {
            await deleteCurrentFolder(folderDeleteState.folder);
            setFolderDeleteState({ open: false, loading: false, folder: null });
          } catch (err) {
            console.error(err);
            setFolderDeleteState((prev) => ({ ...prev, loading: false }));
          }
        }}
        topMessage={translation.deleteFolder}
        warningMessage={
          translation.confirmDeleteFolder
            ? translation.confirmDeleteFolder.replace(
                "{name}",
                folderDeleteState.folder?.name || translation.thisFolder
              )
            : `Are you sure you want to delete "${
                folderDeleteState.folder?.name || translation.thisFolder
              }"?`
        }
        undoMessage={translation.cannotBeUndone}
      />
    </Box>
  );
};

// Main component with provider
class FileManagerApp extends Component {
  render() {
    return (
      <SearchSortProvider>
        <FileManagerAppInner {...this.props} />
      </SearchSortProvider>
    );
  }
}

export default withRouter(FileManagerApp);
FileManagerAppInner.displayName = "FileManagerAppInner";
